import  { useEffect, useState } from 'react';
import {
  Clock, Calendar, BarChart3, CheckCircle, Sun, Gift,
  Settings
} from 'lucide-react';
import { Link } from 'react-router-dom';
import API from '../../api';
import dayjs from 'dayjs';

function Stats_Grid({ user }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const todayDate = dayjs().format('DD-MM-YYYY'); // ✅ आज की तारीख

  useEffect(() => {
    if (!user?.id || !selectedMonth) return;

    const fetchStats = async () => {
      try {
        const [year, month] = selectedMonth.split('-');
        const { data: attendanceData } = await API.get(`/attendance/stats/${user.id}?month=${month}&year=${year}`);
        const { data: projectData } = await API.get(`/customers/projects/counts/${user.id}`);

        setStats({ ...attendanceData, ...projectData });
        setError(null);
      } catch (err) {
        console.error('Failed to load stats:', err);
        setError('Failed to load statistics');
        setStats(null);
      }
    };

    fetchStats();
  }, [user?.id, selectedMonth]);

  const commonCards = stats ? [
    {
      title: "Today's Status",
      icon: <Clock className="w-6 h-6 text-green-600" />,
      iconBg: "bg-green-100",
      value: stats.isPresentToday ? 'Present' : 'Absent'
    },
    {
      title: 'Attendance This Month',
      icon: <CheckCircle className="w-6 h-6 text-orange-600" />,
      iconBg: 'bg-orange-100',
      value: `${stats.attendanceDays} days`
    },
    {
      title: 'Paid Leaves Taken',
      icon: <Calendar className="w-6 h-6 text-blue-600" />,
      iconBg: 'bg-blue-100',
      value: `${stats.paidLeaveDays} days`
    },
    {
      title: 'Holidays (This Month)',
      icon: <Gift className="w-6 h-6 text-pink-600" />,
      iconBg: 'bg-pink-100',
      value: `${stats.holidayCount} days`
    },
    {
      title: 'Sundays',
      icon: <Sun className="w-6 h-6 text-yellow-600" />,
      iconBg: 'bg-yellow-100',
      value: `${stats.sundaysCount} days`
    },
    {
      title: 'Working Days Total',
      icon: <BarChart3 className="w-6 h-6 text-teal-600" />,
      iconBg: 'bg-teal-100',
      value: `${stats.totalWorkingDays} / ${stats.totalDaysInMonth}`
    }
  ] : [];

  const developerCards = stats && user.department === 'Developer' ? [
    {
      title: 'New Projects',
      icon: <CheckCircle className="w-6 h-6 text-indigo-600" />,
      iconBg: 'bg-indigo-100',
      value: stats.newProjects ?? '...',
      to: 'new-project'
    },
    {
      title: 'Old Projects',
      icon: <Calendar className="w-6 h-6 text-gray-600" />,
      iconBg: 'bg-gray-100',
      value: stats.oldProjects ?? '...',
      to: 'old-project'
    }
  ] : [];

  const salesCards = stats && user.department === 'Sales' ? [
    {
      title: "Today's Follow-ups",
      icon: <Clock className="w-6 h-6 text-green-700" />,
      iconBg: 'bg-green-100',
      value: stats.todayFollowups ?? '...',
      to: 'today-sales-followup'
    },
    {
      title: 'Total Follow-ups',
      icon: <BarChart3 className="w-6 h-6 text-blue-700" />,
      iconBg: 'bg-blue-100',
      value: stats.totalFollowups ?? '...',
      to: 'followup-list'
    },
    {
      title: 'Done Projects',
      icon: <CheckCircle className="w-6 h-6 text-purple-700" />,
      iconBg: 'bg-purple-100',
      value: stats.doneProjects ?? '...',
      to: 'done-list'
    }
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
      <div className="hidden md:flex items-center space-x-3">
        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
          <Settings className="w-5 h-5 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome, {user.full_name}
        </h1>
      </div>
      <p className="text-gray-600 text-sm">
        📅 Today's Date: <span className="font-semibold">{todayDate}</span>
      </p>
      </div>

      {error && <div className="text-red-600 font-medium">{error}</div>}

      {!stats ? (
        <div className="text-gray-600">Loading statistics...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...commonCards, ...developerCards, ...salesCards].map((card, index) => (
            <StatCard key={index} {...card} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ title, icon, iconBg, value, to }) {
  const content = (
    <div className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition-shadow">
      <div className="flex items-center space-x-4">
        <div className={`w-12 h-12 ${iconBg} rounded-lg flex items-center justify-center`}>
          {icon}
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">{title}</h3>
          <p className="text-xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );

  return to ? <Link to={`${to}`}>{content}</Link> : content;
}

export default Stats_Grid;
